// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canSubmitForApprovalOrder,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, CreditCardBillingAddressDto, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import PickUpLocationAddressInfoDisplay from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PickUpLocationAddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import CheckoutSectionHeading, {
    CheckoutSectionHeadingStyles,
} from "@keller/App/Components/Checkout/CheckoutSectionHeading";
import Seperator from "@keller/App/Components/Common/SharedPrintStyles";
import { CHECKOUT_STEPS } from "@keller/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import CreditCardAddressInfoDisplay from "@keller/App/Widgets/OrderConfirmation/CreditCardAddressInfoDisplay";
import PickUpShippingAddressInfoDisplay from "@keller/App/Widgets/OrderConfirmation/PickUpShippingAddressInfoDisplay";
import { paymentMethodValueDisplay } from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.helper";
import { checkoutReviewAndSubmitStyle } from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.shared.styles";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import { ShippingAddressInfo } from "@keller/XDL/types";
import { doNothing } from "@keller/XDL/Utilities";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    pickUpDate: Date | null;
    location: WarehouseModel;
    billTo: BillToModel;
    onEditLocation: () => void;
    onEditBillTo: () => void;
    extendedStyles?: PickUpLocationInfoDisplayStyles;
    canGoBack: boolean;
    creditCardBillInformation?: CreditCardBillingAddressDto;
    notes?: string;
    // XNG-Changes:
    shippingAddressInfo?: ShippingAddressInfo;
}

// xng-change
const mapStateToProps = (state: ApplicationState) => ({
    isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
    richContentWidget: getWidgetsByPageId(state, getCurrentPage(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/RichContent";
    })?.fields,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PickUpLocationInfoDisplayStyles {
    container?: GridContainerProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDateHeadingText?: TypographyPresentationProps;
    pickUpDateText?: TypographyPresentationProps;
    locationAddressGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    // XNG-Changes
    gridItem?: GridItemProps;
    labelHeading?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    notesItem?: GridItemProps;
    shippingServiceHeadingText?: TypographyPresentationProps;
    shippingServiceText?: TypographyPresentationProps;
    hideOnPrint?: GridItemProps;
    labelName?: CheckoutSectionHeadingStyles;
    billingAndPickUpSection?: GridContainerProps;
    billingAddressInfoDisplayGridItem?: GridItemProps;
    pickUpLocationAddressInfoDisplayGridItem?: GridItemProps;
    pickUpShippingAddressLocationAddressInfoDisplayGridItem?: GridItemProps;
    orderConfirmationTotalItem?: GridItemProps;
    richContentOnPrintGridItem?: GridItemProps;
    addressGridWrapper?: GridItemProps;
    mainContainer?: GridContainerProps;
}

export const pickUpLocationInfoDisplayStyles: PickUpLocationInfoDisplayStyles = {
    container: { gap: 20 },
    pickUpDateGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    locationAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    billingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    gridItem: {
        ...checkoutReviewAndSubmitStyle.reviewSection.gridItem,
    },
    labelHeading: {
        ...checkoutReviewAndSubmitStyle.reviewSection.label,
    },
    value: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    pickUpDateHeadingText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.label,
    },
    pickUpDateText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    shippingServiceHeadingText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.label,
    },
    shippingServiceText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    notesItem: {
        width: 12,
        css: css`
            &&& {
                width: 100%;
                margin-top: 16px;
            }
        `,
    },
};

const PickUpLocation = ({
    pickUpDate,
    billTo,
    onEditLocation,
    onEditBillTo,
    extendedStyles,
    canGoBack,
    creditCardBillInformation,
    notes,
    // xng-change
    isOrderConfirmationPage,
    richContentWidget,
    shippingAddressInfo,
    ...otherProps
}: Props) => {
    const styles = mergeToNew(pickUpLocationInfoDisplayStyles, extendedStyles);
    // <XNG-Changes>
    const { checkoutInfoState } = useContext(StepperContext);
    const { requiresApproval } = useAppSelector(state => ({
        requiresApproval: canSubmitForApprovalOrder(getCurrentCartState(state).value),
    }));
    // </XNG-Changes>
    return (
        <GridContainer {...styles.container}>
            {/* Addresses section */}
            <CheckoutSectionHeading
                label={translate("Addresses")}
                moveToStep={canGoBack ? CHECKOUT_STEPS.ADDRESS : undefined}
                extendedStyles={styles.labelName}
            />
            <GridItem {...styles.addressGridWrapper}>
                <GridContainer {...styles.billingAndPickUpSection}>
                    <GridItem {...styles.billingAddressInfoDisplayGridItem}>
                        <BillingAddressInfoDisplay
                            billTo={billTo}
                            // XNG-Changes: set doNothing
                            onEdit={doNothing}
                            extendedStyles={styles.billingAddress}
                        />
                    </GridItem>
                    {/* <XNG-Changes> */}
                    {shippingAddressInfo && (
                        <GridItem {...styles.pickUpShippingAddressLocationAddressInfoDisplayGridItem}>
                            <PickUpShippingAddressInfoDisplay pickUpShippingAddress={shippingAddressInfo} />
                        </GridItem>
                    )}
                    {/* </XNG-Changes> */}
                    <GridItem {...styles.pickUpLocationAddressInfoDisplayGridItem}>
                        <PickUpLocationAddressInfoDisplay
                            {...otherProps}
                            // XNG-Changes: set doNothing
                            onEdit={doNothing}
                        />
                    </GridItem>
                    {creditCardBillInformation && (
                        <GridItem {...styles.gridItem} data-test-selector="creditCardBillingAddress">
                            <CreditCardAddressInfoDisplay
                                extendedStyles={styles.billingAddress}
                                creditCardBillInformation={creditCardBillInformation}
                            />
                        </GridItem>
                    )}
                    <GridItem {...styles.notesItem}>
                        <Typography {...styles.shippingServiceHeadingText}>{translate("Order Notes")}</Typography>
                        <Typography {...styles.shippingServiceText}>{notes || EMPTY_VALUE}</Typography>
                    </GridItem>
                    {/* xng-change Rich Content Widget for Order Confirmation Page */}
                    {isOrderConfirmationPage && richContentWidget && (
                        <GridItem {...styles.richContentOnPrintGridItem}>
                            <StyledWrapper>
                                {createWidgetElement("Basic/RichContent", { fields: richContentWidget })}
                            </StyledWrapper>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
            {/* XNG-Changes: show order total summary on print */}
            {isOrderConfirmationPage && (
                <GridItem {...styles.orderConfirmationTotalItem}>
                    <StyledWrapper>
                        {createWidgetElement("OrderConfirmation/OrderConfirmationTotal", { fields: {} })}
                    </StyledWrapper>
                </GridItem>
            )}

            {pickUpDate && (
                <GridItem {...styles.hideOnPrint}>
                    <GridContainer gap={16}>
                        <Seperator />
                        <CheckoutSectionHeading
                            label={translate("Pickup")}
                            moveToStep={canGoBack ? CHECKOUT_STEPS.PAYMENT : undefined}
                        />
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.pickUpDateHeadingText}>{translate("Pick Up Date")}</Typography>
                            <Typography {...styles.pickUpDateText} data-test-selector="requestedPickUpDateValue">
                                <LocalizedDateTime dateTime={pickUpDate} />
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            )}
            {/* <Xng-Changes> */}
            <Seperator />
            {/* </Xng-Changes> */}
            {/* Payment Section */}
            {!requiresApproval ? (
                <GridItem {...styles.hideOnPrint}>
                    <GridContainer gap={16}>
                        <CheckoutSectionHeading
                            label={translate("Payment")}
                            moveToStep={canGoBack ? CHECKOUT_STEPS.PAYMENT : undefined}
                        />
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.labelHeading}>
                                {checkoutInfoState.isCreditCard
                                    ? translate("Credit Card")
                                    : translate("Payment Method")}
                            </Typography>
                            <Typography {...styles.value}>
                                {paymentMethodValueDisplay(checkoutInfoState) || EMPTY_VALUE}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.labelHeading}>{translate("Purchase Order")}</Typography>
                            <Typography {...styles.value}>{checkoutInfoState.poNumber || EMPTY_VALUE}</Typography>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.labelHeading}>{translate("Release Number")}</Typography>
                            <Typography {...styles.value}>{checkoutInfoState.releaseNumber || EMPTY_VALUE}</Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            ) : null}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(PickUpLocation);
